<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'

import AnimatedXButton from '@/components/ui/AnimatedXButton.vue'
import { useEventListener } from '@/composables/useEventListener'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { Z_INDEX } from '@/utils/zIndex'

export interface BaseModalProps {
  isOpen: boolean
  title: string
  description?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  closeable?: boolean
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  showHeader?: boolean
  showFooter?: boolean
  headerClass?: string
  bodyClass?: string
  footerClass?: string
  contentClass?: string
  overlayClass?: string
  preventBodyScroll?: boolean
  trapFocus?: boolean
  autoFocus?: boolean
  restoreFocus?: boolean
  role?: 'dialog' | 'alertdialog'
  ariaModal?: boolean
  zIndex?: number
}

interface BaseModalEmits {
  close: []
  'backdrop-click': []
}

const props = withDefaults(defineProps<BaseModalProps>(), {
  size: 'md',
  closeable: true,
  closeOnBackdropClick: true,
  closeOnEscape: true,
  showHeader: true,
  showFooter: false,
  preventBodyScroll: true,
  trapFocus: true,
  autoFocus: true,
  restoreFocus: true,
  role: 'dialog',
  ariaModal: true,
})

const emit = defineEmits<BaseModalEmits>()

// Refs
const modalRef = ref<HTMLElement | null>(null)
const headerRef = ref<HTMLElement | null>(null)
const bodyRef = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
const previousFocusedElement = ref<HTMLElement | null>(null)

// State
const modalId = ref(`dialog-${Math.random().toString(36).substr(2, 9)}`)

// Computed properties

const titleId = computed(() => `${modalId.value}-title`)
const descriptionId = computed(() => `${modalId.value}-description`)

const sizeClasses = computed(() => {
  switch (props.size) {
    case 'sm':
      return 'max-w-sm'
    case 'md':
      return 'max-w-md'
    case 'lg':
      return 'max-w-lg'
    case 'xl':
      return 'max-w-xl'
    case '2xl':
      return 'max-w-2xl'
    default:
      return 'max-w-md'
  }
})

// Focus management
const getFocusableElements = (): HTMLElement[] => {
  if (!modalRef.value) return []

  const selector = [
    'button:not([disabled]):not([aria-label="Close dialog"])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(', ')

  return Array.from(modalRef.value.querySelectorAll(selector)) as HTMLElement[]
}

const focusFirstElement = () => {
  if (!props.autoFocus) return

  // Skip auto-focus on iOS devices to prevent unwanted keyboard appearance
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return

  nextTick(() => {
    const focusableElements = getFocusableElements()

    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    } else if (modalRef.value) {
      modalRef.value.focus()
    }
  })
}

const restorePreviousFocus = () => {
  if (!props.restoreFocus || !previousFocusedElement.value) return

  nextTick(() => {
    try {
      previousFocusedElement.value?.focus()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.UI, 'Failed to restore focus', { error })
    }
    previousFocusedElement.value = null
  })
}

// Event handlers
const handleClose = () => {
  emit('close')
}

const handleBackdropClick = (event: MouseEvent) => {
  emit('backdrop-click')

  if (props.closeOnBackdropClick && event.target === event.currentTarget) {
    handleClose()
  }
}

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.isOpen && props.closeOnEscape) {
    handleClose()
  }
}

const handleTabTrap = (event: KeyboardEvent) => {
  if (!props.trapFocus || event.key !== 'Tab' || !props.isOpen) return

  const focusableElements = getFocusableElements()

  if (focusableElements.length === 0) return

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault()
    lastElement.focus()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault()
  }
}

// Body scroll management with iOS keyboard handling
const manageBodyScroll = (prevent: boolean) => {
  if (!props.preventBodyScroll) return

  if (prevent) {
    document.body.style.overflow = 'hidden'
    // On iOS, also set position fixed to prevent keyboard-induced viewport shifts
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.top = '0'
    }
  } else {
    document.body.style.overflow = ''
    // Restore body position on iOS
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.style.position = ''
      document.body.style.width = ''
      document.body.style.top = ''
    }
  }
}

// Watchers
watch(
  () => props.isOpen,
  (newValue, oldValue) => {
    if (newValue && !oldValue) {
      // Store previously focused element
      if (props.restoreFocus) {
        previousFocusedElement.value = document.activeElement as HTMLElement
      }

      // Prevent body scroll
      manageBodyScroll(true)

      // Focus management
      focusFirstElement()
    } else if (!newValue && oldValue) {
      // Restore body scroll
      manageBodyScroll(false)

      // Restore previous focus
      restorePreviousFocus()
    }
  },
  { immediate: true },
)

// Event listeners
useEventListener(document, 'keydown', handleEscape)
useEventListener(document, 'keydown', handleTabTrap)

// Cleanup on unmount
onUnmounted(() => {
  manageBodyScroll(false)
})

// Expose methods for child components
defineExpose({
  focus: focusFirstElement,
  getFocusableElements,
  modalRef,
})
</script>

<template>
  <!-- Modal overlay -->
  <Transition
    enter-active-class="transition ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpen"
      :class="[
        // Positioning & Layout
        'fixed',
        'inset-0',
        'flex',
        'items-center',
        'justify-center',
        // Visual
        'backdrop-blur-sm',
        'bg-gray-800/70',
        // Custom overlay classes
        overlayClass,
      ]"
      :style="{ zIndex: zIndex ?? (role === 'alertdialog' ? Z_INDEX.DIALOG : Z_INDEX.MODAL) }"
      :role="role"
      :aria-modal="ariaModal"
      :aria-labelledby="titleId"
      :aria-describedby="description ? descriptionId : undefined"
      @click="handleBackdropClick"
    >
      <!-- Screen reader description -->
      <div v-if="description" :id="descriptionId" class="sr-only">
        {{ description }}
      </div>

      <!-- Modal content -->
      <div
        ref="modalRef"
        :class="[
          // Positioning & Sizing
          'relative',
          'w-full',
          sizeClasses,
          'mx-4',
          // Constrain overall modal height and allow internal scrolling
          'max-h-[85vh]',
          'flex',
          'flex-col',
          // Visual
          'bg-white',
          'rounded-lg',
          'shadow-xl',
          // Custom content classes
          contentClass,
        ]"
        tabindex="-1"
        @click.stop
      >
        <!-- Header -->
        <header
          v-if="showHeader"
          ref="headerRef"
          :class="[
            // Layout
            'flex',
            'items-center',
            'justify-between',
            // Spacing
            'p-4',
            // Border
            'border-b',
            'border-gray-200',
            // Custom header classes
            headerClass,
          ]"
        >
          <div class="flex-1">
            <h2 :id="titleId" class="text-xl font-semibold text-gray-900">
              <slot name="title">{{ title }}</slot>
            </h2>
            <slot name="header-extra" />
          </div>

          <AnimatedXButton
            v-if="closeable"
            ref="closeButtonRef"
            aria-label="Close dialog"
            @click="handleClose"
          />
        </header>

        <!-- Body -->
        <main
          ref="bodyRef"
          :class="[
            // Spacing
            'p-4',
            'flex-1',
            'overflow-y-auto',
            // Custom body classes
            bodyClass,
          ]"
        >
          <slot />
        </main>

        <!-- Footer -->
        <footer
          v-if="showFooter"
          :class="[
            // Spacing
            'p-4',
            // Border
            'border-t',
            'border-gray-200',
            // Layout defaults (content can override)
            'flex',
            'justify-end',
            'gap-3',
            // Custom footer classes
            footerClass,
          ]"
        >
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </Transition>
</template>
